import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"

// Connects to data-controller="settings--epic-print-mapping-export"
export default class extends Controller {

    static targets = [
        "exportButton",
        "spinner",
        "noResultsMessage",
        "form",
        "regionInput",
        "buildingInput",
        "areaInput",
        "confirmModal",
        "warningMessage",
        "regionSelect",
        "buildingSelect",
        "areaSelect"
    ]

    connect() {
        this._modal = null
    }

    disconnect() {
        if (this._modal) {
            this._modal.dispose()
            this._modal = null
        }
    }

    // ==========================================================================
    // Actions
    // ==========================================================================

    /**
     * Phase 1: User clicks Export → AJAX fetch to /validate
     * Validates data and determines next step (no results, warnings, or proceed)
     */
    async startExport(event) {
        event.preventDefault()
        this.hideNoResults()
        this.showSpinner()

        const params = this.buildParams()

        try {
            const response = await fetch('/settings/epic-print-mapping-export/validate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                    'Accept': 'application/json'
                },
                body: JSON.stringify(params)
            })

            if (!response.ok) {
                this.hideSpinner()
                console.error('Validate response error:', response.status, response.statusText)
                const text = await response.text()
                console.error('Response body:', text.substring(0, 500))
                alert(`Export validation failed: ${ response.status } ${ response.statusText }`)
                return
            }

            const data = await response.json()

            if (data.total_workstations === 0) {
                this.hideSpinner()
                this.showNoResults()
                return
            }

            if (data.workstations_missing_epic_id > 0 || data.mappings_missing_printer_epic_id > 0 || data.workstations_missing_dns_name > 0) {
                // Show confirmation modal with warning counts
                this.hideSpinner()
                this.showWarningModal(data)
            } else {
                // No warnings — proceed directly to download
                this.submitExportForm(params)
            }
        } catch (error) {
            this.hideSpinner()
            console.error('Export validation failed:', error)
            alert(`Export validation failed: ${ error.message }`)
        }
    }

    /**
     * Phase 2: User confirms export after seeing warnings
     */
    confirmExport() {
        this.hideWarningModal()
        this.showSpinner()
        const params = this.buildParams()
        this.submitExportForm(params)
    }

    /**
     * User cancels export from confirmation modal
     */
    cancelExport() {
        this.hideWarningModal()
    }

    // ==========================================================================
    // Private Helpers
    // ==========================================================================

    /**
     * Reads selected values from the cascading locale selects.
     * TomSelect stores the selected value on the underlying select element.
     */
    buildParams() {
        const regionId = this.hasRegionSelectTarget ? this.regionSelectTarget.value : ''
        const buildingId = this.hasBuildingSelectTarget ? this.buildingSelectTarget.value : ''
        const areaId = this.hasAreaSelectTarget ? this.areaSelectTarget.value : ''

        return {
            region_id: regionId || null,
            building_id: buildingId || null,
            area_id: areaId || null
        }
    }

    /**
     * Populates hidden form inputs and submits the form to trigger file download.
     * The form POSTs to /settings/epic-print-mapping-export which returns a file.
     */
    submitExportForm(params) {
        this.regionInputTarget.value = params.region_id || ''
        this.buildingInputTarget.value = params.building_id || ''
        this.areaInputTarget.value = params.area_id || ''

        this.formTarget.submit()

        // Hide spinner after a brief delay since file download is async
        // The browser handles the download; we just need to reset the UI
        setTimeout(() => {
            this.hideSpinner()
        }, 1500)
    }

    /**
     * Populates the warning message with missing data counts and shows the Bootstrap modal
     */
    showWarningModal(data) {
        let warningHtml = '<ul class="list-unstyled mb-0">'

        if (data.workstations_missing_epic_id > 0) {
            warningHtml += `<li class="mb-2"><i class="bi bi-exclamation-circle text-warning me-2"></i><strong>${ data.workstations_missing_epic_id }</strong> workstation(s) missing an Epic ID (will export as blank)</li>`
        }

        if (data.mappings_missing_printer_epic_id > 0) {
            warningHtml += `<li class="mb-2"><i class="bi bi-exclamation-circle text-warning me-2"></i><strong>${ data.mappings_missing_printer_epic_id }</strong> print mapping(s) referencing printers without an Epic ID (will export as blank)</li>`
        }

        if (data.workstations_missing_dns_name > 0) {
            warningHtml += `<li class="mb-2"><i class="bi bi-exclamation-circle text-warning me-2"></i><strong>${ data.workstations_missing_dns_name }</strong> workstation(s) missing a DNS name (will export as blank)</li>`
        }

        warningHtml += `<li class="mt-3"><strong>Total workstations to export: ${ data.total_workstations }</strong></li>`
        warningHtml += '</ul>'

        this.warningMessageTarget.innerHTML = warningHtml

        // Show the Bootstrap modal programmatically
        if (!this._modal) {
            this._modal = new bootstrap.Modal(this.confirmModalTarget)
        }
        this._modal.show()
    }

    hideWarningModal() {
        if (this._modal) {
            this._modal.hide()
        }
    }

    showSpinner() {
        if (this.hasSpinnerTarget) {
            this.spinnerTarget.classList.remove('d-none')
        }
        if (this.hasExportButtonTarget) {
            this.exportButtonTarget.disabled = true
        }
    }

    hideSpinner() {
        if (this.hasSpinnerTarget) {
            this.spinnerTarget.classList.add('d-none')
        }
        if (this.hasExportButtonTarget) {
            this.exportButtonTarget.disabled = false
        }
    }

    showNoResults() {
        if (this.hasNoResultsMessageTarget) {
            this.noResultsMessageTarget.classList.remove('d-none')
        }
    }

    hideNoResults() {
        if (this.hasNoResultsMessageTarget) {
            this.noResultsMessageTarget.classList.add('d-none')
        }
    }

}
